if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      this.resizeFrame = null;
      this.mediaResizeObserver = new ResizeObserver(() => this.scheduleViewerHeightUpdate());
      this.handleViewportChange = this.scheduleViewerHeightUpdate.bind(this);
      this.filteringOption = this.dataset.filteringOption;
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      this.mql.addEventListener('change', this.handleViewportChange);
      window.addEventListener('resize', this.handleViewportChange);
      this.observeMediaHeight(this.elements.viewer.querySelector('.product__media-item.is-active'));
      this.scheduleViewerHeightUpdate();
    }

    disconnectedCallback() {
      this.mql.removeEventListener('change', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);
      this.mediaResizeObserver.disconnect();
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
      this.observeMediaHeight(event.detail.currentElement);
      this.scheduleViewerHeightUpdate();
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        // Media are matched to the variant by alt text (case-insensitive); 'always_display' is always shown.
        // If no media matches the selected value, show everything instead of an empty gallery.
        const normalize = (value) => String(value ?? '').trim().toLowerCase();
        const selected = normalize(currentVariant[this.filteringOption]);
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        const hasMatch = Array.from(allSlides).some((slide) => normalize(slide.dataset.alt) === selected);
        allSlides.forEach((slide) => {
          const alt = normalize(slide.dataset.alt);
          slide.classList.toggle('hidden', hasMatch && alt !== selected && alt !== 'always_display');
        });

        const target = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
        if (!target || target.classList.contains('hidden')) {
          const firstVisible = this.elements.viewer.querySelector('.product__media-item[data-media-id]:not(.hidden)');
          if (firstVisible) mediaId = firstVisible.dataset.mediaId;
        }
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');
      this.observeMediaHeight(activeMedia);
      this.scheduleViewerHeightUpdate();

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    observeMediaHeight(media) {
      this.activeMediaForHeight = media;
      this.mediaResizeObserver.disconnect();
      const mediaContainer = media?.querySelector('.product-media-container') || media;
      if (mediaContainer) this.mediaResizeObserver.observe(mediaContainer);
    }

    scheduleViewerHeightUpdate() {
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateViewerHeight();
      });
    }

    updateViewerHeight() {
      const slider = this.elements.viewer?.slider;
      const usesDesktopThumbnails = this.dataset.desktopLayout?.includes('thumbnail');
      if (!slider || !this.mql.matches || !usesDesktopThumbnails) {
        slider?.style.removeProperty('height');
        return;
      }

      const activeMedia = this.activeMediaForHeight?.classList.contains('hidden')
        ? null
        : this.activeMediaForHeight;
      const visibleMedia = activeMedia
        || slider.querySelector('.product__media-item.is-active:not(.hidden)')
        || slider.querySelector('.product__media-item:not(.hidden)');
      const mediaContainer = visibleMedia?.querySelector('.product-media-container') || visibleMedia;
      const mediaHeight = Math.ceil(mediaContainer?.getBoundingClientRect().height || 0);

      if (mediaHeight > 0) slider.style.height = `${ mediaHeight }px`;
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
